import type { ReactNode } from "react";
import { useTheme } from "@/shared/theme/ThemeProvider";
import logoLight from "@/shared/assets/elos-lockup-light.svg";
import logoDark from "@/shared/assets/elos-lockup-dark.svg";

interface AuthCardLayoutProps {
  titulo: string;
  subtitulo?: string;
  /** Frase abaixo da marca — hoje só a tela de Login usa. */
  tagline?: string;
  children: ReactNode;
  /** Linha de rodapé do card (ex.: "Não tem conta? Criar conta"). */
  rodape?: ReactNode;
}

/**
 * Layout compartilhado de Login/Cadastro/Esqueci senha/Redefinir senha
 * (Sprint 3 — redesign Elos, protótipo `proto-login-elos.html` aprovado).
 *
 * Substitui o antigo layout de dois painéis com cor fixa (bg-brand-verde/
 * bege), que não respeitava tema claro/escuro e deixava o botão "Entrar"
 * com a mesma cor do fundo. Agora é um único card centralizado usando os
 * mesmos tokens semânticos do resto do sistema — funciona em dark mode
 * automaticamente e o botão primário sempre tem contraste garantido.
 */
export function AuthCardLayout({ titulo, subtitulo, tagline, children, rodape }: AuthCardLayoutProps) {
  const { theme } = useTheme();

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-lg">
        <div className="mb-7 flex flex-col items-center gap-2 text-center">
          <img
            src={theme === "dark" ? logoDark : logoLight}
            alt="Elos"
            className="h-10 w-auto"
          />
          {tagline && <p className="text-sm text-muted">{tagline}</p>}
        </div>

        <h1 className="text-center text-xl font-bold text-text">{titulo}</h1>
        {subtitulo && <p className="mt-1 text-center text-sm text-muted">{subtitulo}</p>}

        <div className="mt-6">{children}</div>

        {rodape && <p className="mt-6 text-center text-sm text-muted">{rodape}</p>}
      </div>
    </main>
  );
}
